// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { TrailerModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import FlexWrapper, { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { tableProps } from "@store/Themes/common/props/table";
import constants from "@store/constants";
import { TrailersDataViewContext } from "@store/Framework/Store/Greif/Data/Trailers/TrailersSelectors";
import updateSearchFields from "@store/Framework/Store/Greif/Pages/TrailerTracking/Handlers/UpdateSearchFields";
import { historyTableProps } from "@store/Themes/common/props/history";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const ResultCount = getWidgetComponent("TrailerTracking/TrailerTrackingResultCount");

const enum fields {
    shipShipTo = "shipShipTo",
    showTrailerId = "showTrailerId",
    showDwellTime = "showDwellTime",
    showGreifPlant = "showGreifPlant",
    showOrderNumber = "showOrderNumber",
    showItemDescription = "showItemDescription",
}

type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    trailerIdentification: ["trailerIdentification"],
    shipToBp: ["shipToBp"],
    dwellTimeInDays: ["dwellTimeInDays"],
    trailerGreifPlant: ["trailerGreifPlant"],
    trailerCustomerOrder: ["trailerCustomerOrder"],
    trailerItemDescription: ["trailerItemDescription"],
    trailerItem: ["trailerItem"],
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.shipShipTo]: boolean;
        [fields.showTrailerId]: boolean;
        [fields.showDwellTime]: boolean;
        [fields.showGreifPlant]: boolean;
        [fields.showOrderNumber]: boolean;
        [fields.showItemDescription]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    parameter: state.greif.pages.trailerTracking.getTrailersParameter,
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface TrailerTrackingTableStyles {
    container?: InjectableCss;
    headerText?: TypographyProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    loadingOverlay?: LoadingOverlayProps;
    tableWrapper?: InjectableCss;
    headingWrapper?: FlexWrapperPresentationProps;
    trailerItemHeader?: DataTableHeaderProps;
    trailerItemCells?: DataTableCellProps;
    trailerTightHeader?: DataTableHeaderProps;
    trailerTightCells?: DataTableCellProps;
}

export const trailerTrackingTableStyles: TrailerTrackingTableStyles = {
    loadingOverlay: historyTableProps.loadingOverlay,
    container: historyTableProps.container,
    tableWrapper: tableProps.wrapper,
    headingWrapper: historyTableProps.headingWrapper,
    noResultsText: historyTableProps.noResultsText,
    dataTable: historyTableProps.dataTable,
    trailerItemHeader: historyTableProps.orderNumberHeader,
    trailerItemCells: historyTableProps.orderNumberCell,
    trailerTightHeader: historyTableProps.requestedDateHeader,
    trailerTightCells: historyTableProps.requestedDateCell,
};

const styles = trailerTrackingTableStyles;

const TrailerTrackingTable = ({
    parameter,
    updateSearchFields,
    fields: { shipShipTo, showTrailerId, showDwellTime, showGreifPlant, showOrderNumber, showItemDescription },
}: Props) => {
    const baseTrailersDataView = useContext(TrailersDataViewContext);
    const trailersDataView = usePersistentDataView(baseTrailersDataView);
    const [trailerRows, setTrailerRows] = useState<TrailerModel[]>([]);

    const headerClick = (sortField: string) => {
        const previousSort = parameter.sort ?? "";
        const [previousPrimaryField] = previousSort.split(",");
        const direction = previousPrimaryField === sortField ? " DESC" : "";
        const sort = sortByFields[sortField].map(field => `${field}${direction}`).join(", ");
        updateSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (!parameter.sort) {
            return sorted;
        }
        const [primaryField] = parameter.sort.split(",");
        if (primaryField === sortField) {
            sorted = "ascending";
        } else if (primaryField === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    useEffect(() => {
        if (trailersDataView?.value) {
            const rows = trailersDataView?.value?.map(trailer => {
                return {
                    id: trailer.id,
                    trailerIdentification: trailer.trailerIdentification,
                    dwellTimeInDays: trailer.dwellTimeInDays,
                    trailerGreifPlant: trailer.trailerGreifPlant,
                    trailerCustomerOrder: trailer.trailerCustomerOrder,
                    trailerItemDescription: trailer.trailerItemDescription,
                    trailerItem: trailer.trailerItem,
                    shipToBp: trailer.shipToBp,
                };
            });

            setTrailerRows(rows);
        }
    }, [trailersDataView?.value]);

    const isTrueOrUndefined = (value?: boolean) => value === true || value === undefined;

    return (
        <DataViewLoadingOverlay dataView={trailersDataView} {...styles.loadingOverlay}>
            {trailersDataView.value?.length ? (
                <StyledWrapper {...styles.container} data-test-selector="trailerTrackingTable">
                    <FlexWrapper {...styles.headingWrapper}>
                        <ResultCount />
                    </FlexWrapper>
                    <StyledWrapper {...styles.tableWrapper}>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead>
                                {isTrueOrUndefined(shipShipTo) && (
                                    <DataTableHeader
                                        {...styles.trailerTightHeader}
                                        title={translate("Ship To")}
                                        sorted={sorted("shipToBp")}
                                        onSortClick={() => headerClick("shipToBp")}
                                        data-test-selector="tableHeader_quantity"
                                    >
                                        {translate("Ship To", "shipToBp")}
                                    </DataTableHeader>
                                )}
                                {isTrueOrUndefined(showTrailerId) && (
                                    <DataTableHeader
                                        {...styles.trailerTightHeader}
                                        title={translate("Trailer Id")}
                                        sorted={sorted("trailerIdentification")}
                                        onSortClick={() => headerClick("trailerIdentification")}
                                        data-test-selector="tableHeader_trailerIdentification"
                                    >
                                        {translate("Trailer Id", "trailerIdentification")}
                                    </DataTableHeader>
                                )}
                                {isTrueOrUndefined(showDwellTime) && (
                                    <DataTableHeader
                                        {...styles.trailerItemHeader}
                                        title={translate("Dwell Time")}
                                        sorted={sorted("dwellTimeInDays")}
                                        onSortClick={() => headerClick("dwellTimeInDays")}
                                        data-test-selector="tableHeader_dwellTimeInDays"
                                    >
                                        {translate("Dwell Time", "dwellTimeInDays")}
                                    </DataTableHeader>
                                )}
                                {isTrueOrUndefined(showGreifPlant) && (
                                    <DataTableHeader
                                        {...styles.trailerTightHeader}
                                        title={translate("Greif Plant")}
                                        sorted={sorted("trailerGreifPlant")}
                                        onSortClick={() => headerClick("trailerGreifPlant")}
                                        data-test-selector="tableHeader_trailerGreifPlant"
                                    >
                                        {translate("Greif Plant", "trailerGreifPlant")}
                                    </DataTableHeader>
                                )}
                                {isTrueOrUndefined(showOrderNumber) && (
                                    <DataTableHeader
                                        {...styles.trailerTightHeader}
                                        title={translate("Order #")}
                                        sorted={sorted("trailerCustomerOrder")}
                                        onSortClick={() => headerClick("trailerCustomerOrder")}
                                        data-test-selector="tableHeader_trailerCustomerOrder"
                                    >
                                        {translate("PO #", "trailerCustomerOrder")}
                                    </DataTableHeader>
                                )}
                                {isTrueOrUndefined(showItemDescription) && (
                                    <DataTableHeader
                                        {...styles.trailerTightHeader}
                                        title={translate("Item Description")}
                                        sorted={sorted("trailerItemDescription")}
                                        onSortClick={() => headerClick("trailerItemDescription")}
                                        data-test-selector="tableHeader_trailerItemDescription"
                                    >
                                        {translate("Item Description", "trailerItemDescription")}
                                    </DataTableHeader>
                                )}
                            </DataTableHead>
                            <DataTableBody data-test-selector="trailerTrackingTable_tableBody">
                                {trailerRows?.map(
                                    ({
                                        id,
                                        trailerIdentification,
                                        dwellTimeInDays,
                                        shipToBp,
                                        trailerItem,
                                        trailerItemDescription,
                                        trailerGreifPlant,
                                        trailerCustomerOrder,
                                    }) => (
                                        <DataTableRow key={id}>
                                            {isTrueOrUndefined(shipShipTo) && (
                                                <DataTableCell
                                                    {...styles.trailerTightCells}
                                                    data-test-selector="trailerTrackingTable_tableCell_quantityDisplay"
                                                >
                                                    {shipToBp}
                                                </DataTableCell>
                                            )}
                                            {isTrueOrUndefined(showTrailerId) && (
                                                <DataTableCell
                                                    {...styles.trailerTightCells}
                                                    data-test-selector="trailerTrackingTable_tableCell_trailerIdentification"
                                                >
                                                    {trailerIdentification}
                                                </DataTableCell>
                                            )}
                                            {isTrueOrUndefined(showDwellTime) && (
                                                <DataTableCell
                                                    {...styles.trailerItemCells}
                                                    data-test-selector="trailerTrackingTable_tableCell_poNumber"
                                                >
                                                    {translate("{0} days", String(dwellTimeInDays))}
                                                </DataTableCell>
                                            )}
                                            {isTrueOrUndefined(showGreifPlant) && (
                                                <DataTableCell
                                                    {...styles.trailerTightCells}
                                                    data-test-selector="trailerTrackingTable_tableCell_trailerGreifPlant"
                                                >
                                                    {trailerGreifPlant}
                                                </DataTableCell>
                                            )}
                                            {isTrueOrUndefined(showOrderNumber) && (
                                                <DataTableCell
                                                    {...styles.trailerTightCells}
                                                    data-test-selector="trailerTrackingTable_tableCell_trailerCustomerOrder"
                                                >
                                                    {trailerCustomerOrder}
                                                </DataTableCell>
                                            )}
                                            {isTrueOrUndefined(showItemDescription) && (
                                                <DataTableCell
                                                    {...styles.trailerTightCells}
                                                    data-test-selector="trailerTrackingTable_tableCell_trailerItemDescription"
                                                >
                                                    {`${trailerItem} - ${trailerItemDescription}`}
                                                </DataTableCell>
                                            )}
                                        </DataTableRow>
                                    ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography {...styles.noResultsText} data-test-selector="trailerTrackingTable_noTrailersFound">
                        {translate("No trailers found")}
                    </Typography>
                </StyledWrapper>
            )}
        </DataViewLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(TrailerTrackingTable)),
    definition: {
        group: constants.widgetGroup.trailerTracking,
        displayName: "Search Results Table",
        allowedContexts: [constants.pageType.trailerTracking],
        fieldDefinitions: [
            {
                name: fields.shipShipTo,
                displayName: "Ship To",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
            },
            {
                name: fields.showTrailerId,
                displayName: "Trailer ID",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showDwellTime,
                displayName: "Dwell Time",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showGreifPlant,
                displayName: "Grief Plant",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showOrderNumber,
                displayName: "Order Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showItemDescription,
                displayName: "Item Description",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
